import * as React from 'react';
import clsx from 'clsx';

import {ButtonReact} from 'common/components/button';
import {ButtonTheme} from 'common/components/button/constants';
import {SIZE, TEXT_HIGHLIGHT_THEME, TextHighlightReact} from 'common/components/text-highlight';
import type {Theme} from 'common/report/queries/app-api/themes';

import styles from './index.module.styl';

export type SwatchOverflowMenuClickCallback = (params: {action: 'edit' | 'share' | 'delete'; theme: Theme}) => void;

/**
 * Component for seeing a set of colors in a theme, and optionally clicking into colors
 */
export function ColorThemeSwatch({
  theme,
  hideName,
  activeIndex,
  onColorClick,
  onSwatchClick,
  isSelected,
  tag,
  darkMode = undefined,
  overflowMenu,
}: {
  theme: Theme;
  /** hides theme label row (name, tag, overflow menu) */
  hideName?: boolean;
  activeIndex?: number;
  onColorClick?: (index: number) => void;
  onSwatchClick?: () => void;
  isSelected?: boolean;
  tag?: string;
  overflowMenu?: JSX.Element;
  darkMode?: boolean;
}) {
  const canClickColors = Boolean(onColorClick);
  const themeColors = (darkMode && theme.data.darkColors) || theme.data.colors;

  return (
    <div className={clsx(styles.swatch, onSwatchClick && styles.clickable, isSelected && styles.selected)}>
      {!hideName && (
        <div className={clsx(styles.label)}>
          {theme.name}
          {tag ? (
            <TextHighlightReact
              className={styles.tag}
              text={tag}
              size={SIZE.SMALL}
              theme={TEXT_HIGHLIGHT_THEME.GENERIC}
            />
          ) : null}
          {overflowMenu}
        </div>
      )}
      <div className={clsx(styles.swatchColors)} onClick={() => onSwatchClick && onSwatchClick()}>
        {themeColors.map((color, colorIndex) => (
          <ButtonReact
            key={`${theme.id}${colorIndex}`}
            theme={ButtonTheme.Tertiary}
            disabled={!canClickColors}
            readonly={!canClickColors}
            className={clsx(
              styles.item,
              canClickColors && styles.clickable,
              colorIndex === activeIndex && styles.selected,
            )}
            style={{backgroundColor: color}}
            onClick={() => canClickColors && onColorClick?.(colorIndex)}
          />
        ))}
      </div>
    </div>
  );
}
